/**
 * Phase N1a — pure helpers that derive the configurator's chrome from a
 * flow's OWN data instead of literal step-id branches. The step rail and
 * the merch category tabs derive from the flow's own option steps + their
 * distinct `option.category` values, generalising cleanly to any
 * admin-authored box.
 *
 * Phase N2b — multi-select is now driven by the authored per-step
 * `maxSelections` (schema convention `0 = unlimited; 1 = single; N = max-N`)
 * via `orderPipeline.getMaxSelections`, REPLACING N1a's index rule (first
 * step single, rest unlimited). N1a's index rule was a quirk-preservation
 * stopgap noted as N2 scope; the editor now authors real caps (N2a). The
 * static visual baseline is unaffected — the cap only governs interaction
 * (how many options a customer can pick), not the rendered option grid.
 */

import type { FlowConfig, Step } from '../types';
import { getMaxSelections } from './orderPipeline';

/** True when a step renders the option grid (default type or explicit 'options'). */
export function isOptionsStep(step: Step): boolean {
  return !step.type || step.type === 'options';
}

/**
 * The steps shown in the numbered step rail at the top of the configurator:
 * every option-type step, in order. (form/upload/summary steps are not part
 * of the rail — they render their own bespoke UI.)
 */
export function getRailSteps(flow: FlowConfig): Step[] {
  return flow.steps.filter(isOptionsStep);
}

/**
 * Distinct `option.category` values within a step, in first-seen order.
 * Drives the merch category tabs. Returns `[]` when no option carries a
 * category (e.g. packaging / final-touch) — the caller then renders no tab
 * rail, exactly like today.
 */
export function getStepCategories(step: Step): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const opt of step.options ?? []) {
    const cat = opt.category;
    if (cat && !seen.has(cat)) {
      seen.add(cat);
      out.push(cat);
    }
  }
  return out;
}

/** A step shows category tabs iff it has 2+ distinct option categories. */
export function stepHasCategoryTabs(step: Step): boolean {
  return getStepCategories(step).length >= 2;
}

/**
 * Whether a step lets the customer pick more than one option, driven by the
 * authored `maxSelections` (`0 = unlimited; 1 = single; N = max-N`). The
 * `flow` argument is retained for signature compatibility with N1a callers
 * but is no longer consulted (the rule is purely per-step now). Non-options
 * steps are never multi-select.
 */
export function isMultiSelectStep(_flow: FlowConfig, step: Step): boolean {
  if (!isOptionsStep(step)) return false;
  return getMaxSelections(step) > 1;
}
